import styled, { css } from 'styled-components';

export const Container = styled.div<{ $fullScreenVideo?: boolean }>(
  ({ theme, $fullScreenVideo }) => css`
    align-items: ${$fullScreenVideo ? 'flex-start' : 'center'};
    display: flex;
    flex-direction: column;
    width: 100%;
    ${
      $fullScreenVideo &&
      css`
        /* Anchor VideoTestFooter inside the card column, not under side controls. */
        position: relative;
        flex: 1;
        min-height: 0;

        /* Fill the viewport on mobile so bottom-pinned options sit under the video. */
        @media (max-width: ${theme.sz.md}) {
          min-height: calc(
            100dvh - ${theme.sz.s12} - ${theme.sz.s16} - env(safe-area-inset-bottom, 0px)
          );
        }
      `
    }

    & > * {
      margin-bottom: ${theme.sz.s6};
    }

    & > *:last-child {
      margin-bottom: 0;
    }
  `,
);

export const Instruction = styled.h2<{ $onVideo?: boolean }>(
  ({ theme, $onVideo }) => css`
    color: ${$onVideo ? theme.co.neutral99 : theme.co.secondary0};
    display: none;
    font-size: ${theme.DEPRECATED_ty.larger};
    font-weight: ${theme.DEPRECATED_ty.mediumWeight};
    margin-top: 0;
    max-width: 100%; /* overcome global limit to left align on larger screens */
    width: 100%;
    ${
      $onVideo &&
      css`
        text-shadow: 0 0.125rem 0.75rem rgba(0, 0, 0, 0.45);
      `
    }

    @media (min-width: 40rem) {
      display: block;
    }

    @media (min-width: 60rem) {
      font-size: ${theme.DEPRECATED_ty.largerStill};
    }
  `,
);

export const AttributesContainer = styled.div(
  ({ theme }) => css`
    margin-top: ${theme.sz.s2};
    margin-bottom: ${theme.sz.s6};
    text-align: center;
  `,
);

// On UGC courses there can be lots of attributes with quite long
// copy, the inline-block makes them use their natural size and
// word-wrap (both between pills and inside the pill).
export const Attribute = styled.div(
  ({ theme }) => css`
    display: inline-block;
    margin-bottom: ${theme.sz.s1};
    margin-right: ${theme.sz.s1};
  `,
);

export const PostAnswerInfo = styled.div(
  ({ theme }) => css`
    display: flex;
    width: 100%;
    padding: ${theme.sz.s4};
    font-size: ${theme.DEPRECATED_ty.medium};
    background-color: ${theme.co.neutral95};
    color: ${theme.co.neutral20};
    border-radius: ${theme.sz.s4};
    margin-bottom: ${theme.sz.s1};
    justify-content: flex-start;
    & > span {
      margin-top: 0;
    }

    @media (min-width: 60rem) {
      margin-bottom: ${theme.sz.s2};
    }
  `,
);

export const PostAnswerLabel = styled.span(
  ({ theme }) => css`
    font-weight: ${theme.DEPRECATED_ty.bold};
    text-transform: uppercase;
    padding-right: ${theme.sz.s2};
  `,
);

export const InfoContainer = styled.div(
  ({ theme }) => css`
    display: none;
    width: 100%;
    margin-top: 0;

    @media (min-width: 40rem) {
      display: flex;
      justify-content: space-between;
      margin-bottom: ${theme.sz.s2};
    }
  `,
);

export const VideoTestFooter = styled.div(
  ({ theme }) => css`
    box-sizing: border-box;
    width: 100%;
    z-index: ${theme.zi.z2};
    display: flex;
    flex-direction: column;
    gap: ${theme.sz.s3};
    /* Anchored top and bottom so tall responses (e.g. big keyboards) are capped
       to the card height instead of growing off the top of the screen. */
    pointer-events: none;

    /* Mobile: pin to the bottom so the video stays visible and thumbs reach the options. */
    @media (max-width: ${theme.sz.md}) {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      justify-content: flex-end;
      padding-bottom: max(${theme.sz.s3}, env(safe-area-inset-bottom, 0px));
    }

    /* Desktop: bottom-right cluster — next action left of the options. The top
       offset keeps tall options clear of the instruction and the side controls
       docked in the top-right corner. */
    @media (min-width: ${theme.sz.md}) {
      position: absolute;
      top: ${theme.sz.s28};
      right: 0;
      bottom: ${theme.sz.s4};
      flex-direction: row;
      justify-content: flex-end;
      align-items: flex-end;
      gap: ${theme.sz.s3};
      width: auto;
      max-width: 100%;
    }

    @media (min-width: ${theme.sz.ml}) {
      bottom: ${theme.sz.s8};
    }
  `,
);

export const VideoTestNextAction = styled.div<{ $belowResponseOnMobile?: boolean }>(
  ({ theme, $belowResponseOnMobile }) => css`
    flex-shrink: 0;
    display: flex;
    justify-content: flex-end;

    /* A tall typing panel would push the action to the top of the screen,
       away from the keys, so keep it under the panel instead. */
    @media (max-width: ${theme.sz.md}) {
      order: ${$belowResponseOnMobile ? 1 : 0};
    }

    @media (min-width: ${theme.sz.md}) {
      justify-content: flex-start;
    }
  `,
);

export const VideoTestToolbar = styled.div(
  ({ theme }) => css`
    display: flex;
    justify-content: center;
    width: 100%;
    flex-shrink: 0;

    /* Keep the dark control pill compact when GrowthLevelAndToggles is embedded. */
    [data-testid='test-video-toggle-overlay'] {
      margin: 0;
    }

    @media (min-width: ${theme.sz.md}) {
      display: none;
    }
  `,
);

export const VideoTestColumn = styled.div(
  ({ theme }) => css`
    box-sizing: border-box;
    width: 100%;
    z-index: ${theme.zi.z2};
    display: flex;
    flex-direction: column;
    min-height: 0;
    max-height: 100%;

    /* Slightly narrower than the old answer column so options stay mostly over
       the right-side blur rather than the sharp subject. */
    @media (min-width: ${theme.sz.md}) {
      width: min(calc(${theme.sz.ms} - ${theme.sz.s20}), 100%);
    }
  `,
);

export const PromptAndResponseContainer = styled.div<{
  $isGridLayout: boolean;
}>(
  ({ theme, $isGridLayout }) => css`
    @media (max-width: ${theme.sz.md}) {
      display: ${$isGridLayout && 'grid'};
    }
  `,
);
